// QR location data lives in @/lib/qr/decode so qr-review (and any future
// consumer) can share the same shape without cross-feature imports.
import type { QrBboxData } from '@/lib/qr/decode';

export type { QrBboxData };

// ─── Batch Domain Types ─────────────────────────────────────────────────────

export type BatchStatus = 'pending' | 'confirmed' | 'in_progress' | 'completed';
// QA hand-off state, tracked on the workload assignment separately from
// `status`. A non-null value means the container has left the scanning
// operator's hands for the QA pipeline — the execution page goes read-only.
export type QaStatus = 'pending_qa' | 'in_qa' | 'qa_passed' | 'qa_failed';
export type PageStatus = 'success' | 'failed' | 'fragile';
export type ScannerStatus = 'online' | 'offline' | 'maintenance';
export type ItemType = 'box' | 'container' | 'page';
export type ContainerValidationStatus = 'pending' | 'validated' | 'failed';

// Status of a single repair cycle, and (via `ScanBatch.repair_status`) of the
// latest one. Shares wording with the Oman Repair feature's `OmanRepairStatus`
// — kept as a local literal union (not a cross-feature import) since the two
// features don't share a barrel export for it.
export type RepairCycleStatus = 'waiting' | 'completed' | 'returned';

export interface RepairHistoryEntry {
  repair_status: RepairCycleStatus;
  notes: string | null; // reason given when sending to repair
  repair_comment: string | null; // comment entered on completion/return
  transferred_at: string | null; // ISO8601
  // Whitelisted actor who created this transfer (sent to repair / returned it).
  created_by: { id: number; name: string } | null;
  // Whitelisted actor who wrote `repair_comment` (confirmed the repair).
  confirmed_by: { id: number; name: string } | null;
}

export interface ScanBatch {
  id: number;
  batch_number: string; // e.g. BCH-2024-0312
  box_id: string | null;
  container_id: string; // e.g. CNT-002
  // Linked registration id — needed to send a batch to the repair queue
  // (POST /photo-scanning/{registration}/refuse). Null when the batch has no
  // registration yet.
  registration_id: number | null;
  // Mirrors the linked registration's dynamic_form.is_double_sided. When
  // true the scanner runs in duplex mode (front + back); when false it
  // scans a single side per sheet. Operators no longer override this.
  is_double_sided?: boolean;
  client_name: string;
  scanner_id: string; // hardware ID e.g. KDK-01
  scanner_model: string; // e.g. Kodak i5850
  scanner_status: ScannerStatus;
  region: string;
  status: BatchStatus;
  // Null until the batch is transferred to QA. Once set, scanning actions
  // (transfer, repair, page delete/re-image, rotate, rearrange) are locked.
  qa_status: QaStatus | null;
  total_pages: number;
  scanned_pages: number;
  success_count: number;
  failed_count: number;
  fragile_count: number;
  assigned_to: number | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  pdf_path: string | null;
  pdf_url: string | null; // signed GCS URL for the batch PDF
  /** What the backend's upload classifier accepts — classify decoded page-QRs
   *  against this, never against `containers`. */
  expected_container_numbers?: string[];
  containers?:
    | {
        id: number;
        container_number: string;
        type: string | null;
      }[]
    | null;
  operator?: {
    id: number;
    name: string;
  } | null;
  repair_count: number; // total times ever sent to repair, any status
  // Status of the LATEST repair cycle; null if never sent to repair.
  repair_status: RepairCycleStatus | null;
  // Full repair history, newest first. Empty array if never sent to repair.
  repair_history: RepairHistoryEntry[];
}

export interface BatchPageRecord {
  id: number;
  batch_id: number;
  sequence: number; // e.g. 1, 2, 3…
  page_number: string; // e.g. PG-001
  barcode: string | null; // QR payload printed during the sticking stage e.g. CNT-2024-001-P1
  status: PageStatus;
  // success     — QR decoded and matches this container's expected sticking labels
  // mismatched  — QR decoded as a valid page-QR but belongs to a different container
  // unverified  — image stored but no QR could be decoded (or it wasn't a page QR)
  // failed      — legacy generic failure
  qr_status: 'success' | 'mismatched' | 'unverified' | 'failed';
  /** Persisted location of the printed QR — null for rows uploaded before this
   *  was captured, or when the frontend couldn't decode anything at all. */
  qr_bbox?: QrBboxData | null;
  dpi: number; // e.g. 600
  scan_time_ms: number; // milliseconds
  item_type: ItemType; // returned by backend BatchPageRecordResource
  file_ref: string; // partial path e.g. SAFHA_CNT-002_BCH-2024-0312_001
  image_url: string | null; // signed GCS URL — null when file_ref is empty or a scanned:// placeholder
  thumbnail_url?: string | null;
  replaced?: boolean; // transient flag — true when this scan replaced an existing slot
  scanned_at: string;
}

export interface ScanBatchMetrics {
  total: number;
  trend_percent: number; // e.g. 12 means +12%
}

export function isProblemPage(p: BatchPageRecord): boolean {
  return (
    p.qr_status === 'mismatched' ||
    p.qr_status === 'unverified' ||
    p.status === 'failed'
  );
}

// ─── List / Query Params ─────────────────────────────────────────────────────

export interface ScanBatchListParams {
  page?: number;
  per_page?: number;
  search?: string;
  status?: BatchStatus | BatchStatus[];
  operator_id?: number | number[];
  device_id?: number | number[];
  container_number?: string;
  // 'never' = zero repair cycles ever; the other three filter by the LATEST
  // cycle's status.
  repair_status?: Array<RepairCycleStatus | 'never'>;
  min_repair_count?: number;
  max_repair_count?: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}
